import { useMemo, useState } from 'react';
import { RefreshIconButton } from '../../../shared/ui/RefreshIconButton.jsx';
import { DororaidIcon } from '../../../shared/ui/DororaidIcon.jsx';
import { useNikkeCatalog } from '../../nikke-catalog/hooks/useNikkeCatalog.js';
import { NikkeDeckDisplay } from '../../nikke-catalog/shared/NikkeDeckDisplay.jsx';
import {
  filterRecommendedDecks,
  RECOMMENDED_DECK_FILTERS,
} from '../domain/recommendedDecks.js';
import { useRecommendedDecks } from '../hooks/useRecommendedDecks.js';
import { RecommendedDeckEditor } from './RecommendedDeckEditor.jsx';

function nextElementFilter(current, selected) {
  if (selected === '전체') return '전체';
  return current === selected ? '전체' : selected;
}

function eagerCardCount(mobile) {
  return mobile ? 2 : 3;
}

function highPriorityCardCount(mobile) {
  return mobile ? 1 : 2;
}

export function RecommendedDeckBoard({
  mobile = false,
  canManage = false,
  unionId = '',
  unionName = '',
  viewerRole = '',
}) {
  const decks = useRecommendedDecks({ unionId, unionName, viewerRole });
  const { catalog, loading: catalogLoading } = useNikkeCatalog();
  const [element, setElement] = useState('전체');
  const [query, setQuery] = useState('');
  const [editor, setEditor] = useState(null);

  // Every matching deck is drawn at once: a union keeps a few dozen, and the lightweight cards make
  // that cheap. Only the first cards' portraits are fetched eagerly; the rest load as they scroll in.
  const visible = useMemo(
    () => filterRecommendedDecks(decks.decks, element, query),
    [decks.decks, element, query],
  );
  const roleCanManage = ['leader', 'deputy', 'owner', 'admin'].includes(String(decks.viewerRole || viewerRole || ''));
  const editable = Boolean(canManage || roleCanManage);

  const showFatalError = Boolean(decks.errorMessage && !decks.loading && !decks.decks.length);
  if (showFatalError) {
    return <section className={`recommended-decks-state error ${mobile ? 'mobile' : ''}`}><strong>추천덱을 불러오지 못했습니다.</strong><p>{decks.errorMessage}</p><button type="button" onClick={() => decks.refresh()}>다시 확인</button></section>;
  }

  return (
    <section className={`recommended-decks-board ${mobile ? 'mobile' : 'desktop'}`} aria-label="유니온 추천덱">
      <div className="recommended-decks-toolbar recommended-decks-toolbar-primary">
        <div className="recommended-decks-tabs" role="tablist" aria-label="추천 속성 필터">
          {RECOMMENDED_DECK_FILTERS.map((item) => <button type="button" role="tab" aria-selected={element === item} className={element === item ? 'active' : ''} key={item} onClick={() => setElement(nextElementFilter(element, item))}>{item}</button>)}
        </div>
        <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="추천덱 이름·메모 검색" />
        <div className="recommended-decks-actions">
          <span>{visible.length}/{decks.decks.length}</span>
          <RefreshIconButton onClick={() => decks.refresh()} disabled={decks.loading} />
          {editable ? <button type="button" className="primary" onClick={() => setEditor({})}>추천덱 추가</button> : null}
        </div>
      </div>

      {decks.loading ? <p className="recommended-decks-inline-status" role="status"><span aria-hidden="true" />추천덱을 불러오는 중입니다. 다른 메뉴는 계속 사용할 수 있습니다.</p> : null}
      {!decks.loading && catalogLoading && decks.decks.length ? <p className="recommended-decks-inline-status subtle" role="status">니케 이미지를 준비하는 중입니다.</p> : null}
      {decks.errorMessage && decks.decks.length ? <p className="recommended-decks-notice error">{decks.errorMessage}</p> : null}
      {decks.notice.message ? <p className={`recommended-decks-notice ${decks.notice.type}`}>{decks.notice.message}</p> : null}

      <div className="recommended-decks-grid">
        {visible.map((deck, index) => (
          <article className="recommended-deck-card" key={deck.id}>
            <header>
              <div><span>{deck.element}</span><h3>{deck.title}</h3></div>
              {editable ? (
                <div className="recommended-deck-card-actions" role="group" aria-label={`${deck.title} 추천덱 동작`}>
                  <button type="button" className="recommended-deck-icon-action edit" aria-label="추천덱 수정" title="추천덱 수정" onClick={() => setEditor(deck)}><DororaidIcon name="edit" size={mobile ? 18 : 16} /></button>
                  <button type="button" className="recommended-deck-icon-action danger" aria-label="추천덱 삭제" title="추천덱 삭제" disabled={Boolean(decks.working)} onClick={async () => { if (!window.confirm(`${deck.title} 추천덱을 삭제할까요?`)) return; await decks.remove(deck.id); }}><DororaidIcon name="close" size={mobile ? 18 : 16} /></button>
                </div>
              ) : null}
            </header>
            <NikkeDeckDisplay
              deck={deck.deck}
              mobile={mobile}
              catalog={catalog}
              loading={index < eagerCardCount(mobile) ? 'eager' : 'lazy'}
              fetchPriority={index < highPriorityCardCount(mobile) ? 'high' : 'auto'}
              lightweight
            />
            <p>{deck.note || '등록된 메모가 없습니다.'}</p>
          </article>
        ))}
        {!visible.length && !decks.loading ? <div className="recommended-decks-empty"><strong>조건에 맞는 추천덱이 없습니다.</strong><p>{editable ? '새 추천덱을 등록하거나 필터를 변경해주세요.' : '운영자가 추천덱을 등록하면 이곳에 표시됩니다.'}</p></div> : null}
      </div>

      {editor ? <RecommendedDeckEditor deck={editor.id ? editor : null} mobile={mobile} working={decks.working} onSave={decks.save} onClose={() => setEditor(null)} /> : null}
    </section>
  );
}
